/**
 * Servicio para manejar acciones en modo offline y gestión de caché
 */

/**
 * Verifica si hay conexión a internet
 * @returns {boolean} true si hay conexión, false si no
 */
export const isOnline = (): boolean => {
  return navigator.onLine;
};

/**
 * Limpia todo el caché de la aplicación y la recarga
 * - Elimina todas las cachés del navegador
 * - Limpia localStorage (preservando configuraciones importantes)
 * - Limpia sessionStorage
 * - Desregistra Service Workers
 * - Recarga la aplicación con un parámetro de tiempo para evitar caché
 */
export const clearCacheAndReload = async (): Promise<void> => {
  try {
    // Mostrar un mensaje de carga
    const loadingMessage = document.createElement('div');
    loadingMessage.style.position = 'fixed';
    loadingMessage.style.top = '0';
    loadingMessage.style.left = '0';
    loadingMessage.style.width = '100%';
    loadingMessage.style.height = '100%';
    loadingMessage.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    loadingMessage.style.display = 'flex';
    loadingMessage.style.justifyContent = 'center';
    loadingMessage.style.alignItems = 'center';
    loadingMessage.style.zIndex = '9999';
    loadingMessage.style.color = 'white';
    loadingMessage.style.fontSize = '18px';
    loadingMessage.style.textAlign = 'center';
    loadingMessage.style.padding = '20px';
    loadingMessage.innerHTML = `
      <div>
        <div style="margin-bottom: 20px;">Actualizando la aplicación...</div>
        <div style="width: 50px; height: 50px; border: 5px solid #f3f3f3; border-top: 5px solid #3498db; border-radius: 50%; margin: 0 auto; animation: spin 1s linear infinite;"></div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    document.body.appendChild(loadingMessage);

    // Limpiar caché de la aplicación usando la API Cache Storage
    if ('caches' in window) {
      try {
        // Obtener todas las cachés disponibles
        const cacheNames = await caches.keys();
        console.log('Cachés encontradas:', cacheNames);

        // Eliminar todas las cachés una por una
        const deletionPromises = cacheNames.map(async cacheName => {
          try {
            const deleted = await caches.delete(cacheName);
            console.log(`Caché ${cacheName} eliminada: ${deleted}`);
            return deleted;
          } catch (err) {
            console.error(`Error al eliminar caché ${cacheName}:`, err);
            return false;
          }
        });

        const results = await Promise.all(deletionPromises);
        console.log('Resultados de eliminación de cachés:', results);
      } catch (cacheError) {
        console.error('Error al acceder a las cachés:', cacheError);
      }
    }

    // Limpiar IndexedDB
    try {
      const databases = await indexedDB.databases();
      console.log('Bases de datos IndexedDB encontradas:', databases);

      for (const db of databases) {
        if (db.name) {
          try {
            await indexedDB.deleteDatabase(db.name);
            console.log(`Base de datos IndexedDB ${db.name} eliminada`);
          } catch (dbError) {
            console.error(`Error al eliminar base de datos ${db.name}:`, dbError);
          }
        }
      }
    } catch (idbError) {
      console.error('Error al acceder a IndexedDB:', idbError);
    }

    // Limpiar localStorage (excepto configuraciones importantes como el tema oscuro)
    try {
      const darkMode = localStorage.getItem('darkMode');
      const keysToPreserve = ['darkMode'];

      // Guardar valores que queremos preservar
      const preservedValues: Record<string, string | null> = {};
      keysToPreserve.forEach(key => {
        preservedValues[key] = localStorage.getItem(key);
      });

      // Limpiar localStorage
      localStorage.clear();
      console.log('localStorage limpiado');

      // Restaurar valores preservados
      Object.entries(preservedValues).forEach(([key, value]) => {
        if (value !== null) {
          localStorage.setItem(key, value);
        }
      });
    } catch (lsError) {
      console.error('Error al limpiar localStorage:', lsError);
    }

    // Limpiar sessionStorage
    try {
      sessionStorage.clear();
      console.log('sessionStorage limpiado');
    } catch (ssError) {
      console.error('Error al limpiar sessionStorage:', ssError);
    }

    // Si hay un Service Worker registrado, desregistrarlo
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log('Service Workers encontrados:', registrations.length);

        const unregisterPromises = registrations.map(async registration => {
          try {
            const success = await registration.unregister();
            console.log(`Service Worker desregistrado: ${success}`);
            return success;
          } catch (swError) {
            console.error('Error al desregistrar Service Worker:', swError);
            return false;
          }
        });

        const results = await Promise.all(unregisterPromises);
        console.log('Resultados de desregistro de Service Workers:', results);
      } catch (swError) {
        console.error('Error al acceder a los Service Workers:', swError);
      }
    }

    // Limpiar caché de aplicación usando la API de Application Cache (para navegadores antiguos)
    if ('applicationCache' in window) {
      try {
        // @ts-ignore - applicationCache está obsoleto pero aún puede existir en algunos navegadores
        window.applicationCache.swapCache();
        console.log('Application Cache limpiado');
      } catch (appCacheError) {
        console.error('Error al limpiar Application Cache:', appCacheError);
      }
    }

    // Limpiar caché de fetch
    try {
      if ('fetch' in window) {
        const cacheBusterUrl = `${window.location.origin}/clear-cache-dummy?t=${Date.now()}`;
        await fetch(cacheBusterUrl, { cache: 'reload', mode: 'no-cors' });
        console.log('Caché de fetch limpiado');
      }
    } catch (fetchError) {
      console.error('Error al limpiar caché de fetch:', fetchError);
    }

    // Recargar la página con cache-busting
    console.log('Recargando la aplicación...');
    setTimeout(() => {
      // Usar location.replace para evitar que el usuario pueda volver atrás a la versión cacheada
      window.location.replace(window.location.origin + '?t=' + new Date().getTime());
    }, 2000); // Aumentamos el tiempo para asegurar que todas las operaciones de limpieza se completen

  } catch (error) {
    console.error('Error general al limpiar el caché:', error);
    // En caso de error, intentar una recarga simple
    alert('Hubo un problema al actualizar la aplicación. Se intentará recargar.');
    window.location.reload();
  }
};

// Función para verificar si una URL es accesible
export const isUrlAccessible = async (url: string): Promise<boolean> => {
  if (!isOnline()) {
    return false;
  }

  try {
    await fetch(url, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    return true;
  } catch (error) {
    console.error('Error checking URL accessibility:', error);
    return false;
  }
};

// Lista de acciones que requieren conexión a internet
export const OFFLINE_RESTRICTED_ACTIONS = {
  SCAN_QR: 'escanear códigos QR',
  SUBMIT_FORM: 'enviar formularios',
  DOWNLOAD_CONTENT: 'descargar contenido',
  UPDATE_PROFILE: 'actualizar perfil',
  REGISTER_ATTENDANCE: 'registrar asistencia',
  SYNC_DATA: 'sincronizar datos',
  ASK_QUESTION: 'enviar preguntas',
  LIKE_CONTENT: 'dar "me gusta"',
  COMMENT: 'comentar',
  SHARE_CONTENT: 'compartir contenido',
  VOTE_POLL: 'votar en encuestas',
};

// Interfaz para el evento de acción offline
export interface OfflineActionEvent {
  action: string;
  timestamp: number;
  data?: any;
}

// Función para disparar un evento de acción offline
export const triggerOfflineActionEvent = (action: string, data?: any): void => {
  const event = new CustomEvent('offlineAction', {
    detail: {
      action,
      timestamp: Date.now(),
      data,
    } as OfflineActionEvent,
  });
  window.dispatchEvent(event);
};

// Función para verificar si una acción requiere conexión y mostrar una notificación si es necesario
export const checkOfflineAction = (
  action: string,
  callback: () => void,
  offlineCallback?: () => void
): void => {
  if (isOnline()) {
    // Si hay conexión, ejecutar la acción normalmente
    callback();
  } else {
    // Si no hay conexión, disparar un evento para mostrar una notificación
    triggerOfflineActionEvent(action);

    // Si se proporciona un callback para modo offline, ejecutarlo
    if (offlineCallback) {
      offlineCallback();
    }
  }
};

// Función para guardar una acción para sincronización posterior
export const queueOfflineAction = async (
  action: string,
  data: any
): Promise<void> => {
  try {
    // Aquí se podría implementar la lógica para guardar la acción en IndexedDB
    console.log(`Acción "${action}" guardada para sincronización posterior:`, data);

    // Disparar un evento para notificar que la acción se ha guardado
    const event = new CustomEvent('offlineActionQueued', {
      detail: {
        action,
        timestamp: Date.now(),
        data,
      },
    });
    window.dispatchEvent(event);
  } catch (error) {
    console.error(`Error al guardar la acción "${action}" para sincronización posterior:`, error);
  }
};
